import { QueryClient } from "@tanstack/react-query";
import { ULogger } from "@/app/utils/ULogger";

export class UPerf {
  static readonly QUERY_PARAM = "perf";
  static readonly STORAGE_KEY = "meidly_perf";

  static clientEnabled(): boolean {
    if (typeof window === "undefined") {
      return false;
    }
    try {
      if (new URLSearchParams(window.location.search).has(UPerf.QUERY_PARAM)) {
        return true;
      }
      return localStorage.getItem(UPerf.STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  }

  // Logs each React Query fetch with its offset from the start of the current burst,
  // so a serialized waterfall (staggered offsets) is distinguishable from truly parallel
  // fetches (offsets near 0) where the DB / cold start is the real cost.
  static trackQueryCache(client: QueryClient): () => void {
    if (!UPerf.clientEnabled()) {
      return () => {};
    }

    const cache = client.getQueryCache();
    const starts = new Map<string, number>();
    let burstStart = 0;
    let active = 0;

    return cache.subscribe((event) => {
      const query = event.query;
      const hash = query.queryHash;
      const isFetching = query.state.fetchStatus === "fetching";

      if (isFetching && !starts.has(hash)) {
        const now = performance.now();
        if (active === 0) {
          burstStart = now;
        }
        active++;
        starts.set(hash, now);
        return;
      }

      if (!isFetching && starts.has(hash)) {
        const now = performance.now();
        const start = starts.get(hash) ?? now;
        starts.delete(hash);
        active = Math.max(0, active - 1);
        ULogger.info("[perf] query", {
          key: JSON.stringify(query.queryKey),
          startOffsetMs: Math.round(start - burstStart),
          durationMs: Math.round(now - start),
        });
      }
    });
  }
}
